/**
 * Candidate Cloning Utilities
 *
 * Provides safe deep cloning for Candidate objects, avoiding common pitfalls
 * with JSON.parse/stringify (loss of undefined, Date conversion, etc.)
 */

import { StateManager } from '~/engine/processing/shared/state-manager';
import type { BondPaymentCashFlowRecord, Candidate, CashFlow } from '~/types';

/**
 * Creates a deep clone of a Candidate object.
 *
 * This function properly handles:
 * - Nested objects (cashFlows, bondPayments, millageInformation)
 * - Arrays at all levels
 * - Preserves undefined values (unlike JSON.parse/stringify)
 * - Maintains type safety
 *
 * Note: Date strings remain as strings (ISO format) which is the expected format
 * in Candidate objects. If actual Date objects were used, they would need special handling.
 *
 * @param candidate - The candidate to clone
 * @returns A new Candidate object with all nested data deep copied
 */
export function cloneCandidate(candidate: Candidate): Candidate {
  return {
    ...candidate,
    // Deep clone cash flows array
    cashFlows: candidate.cashFlows?.map(cloneCashFlow) ?? [],
    // Deep clone millage information
    millageInformation: candidate.millageInformation
      ? {
          ...candidate.millageInformation,
          millageMap: { ...candidate.millageInformation.millageMap },
          millageShape: {
            ...candidate.millageInformation.millageShape,
            intervals: candidate.millageInformation.millageShape.intervals?.map((interval) => ({
              ...interval,
            })),
          },
        }
      : candidate.millageInformation,
    // Deep clone revenue map
    revenueMap: candidate.revenueMap ? { ...candidate.revenueMap } : {},
    // Deep clone results
    results: candidate.results
      ? {
          ...candidate.results,
          failureMessages: [...(candidate.results.failureMessages || [])],
          bondSeriesResults: candidate.results.bondSeriesResults?.map((bsr) => ({ ...bsr })) || [],
        }
      : candidate.results,
    // Deep clone optimization history
    optimizationHistory: candidate.optimizationHistory?.map((entry) => ({
      ...entry,
      details: entry.details ? { ...entry.details } : undefined,
    })),
    // Deep clone tax year plan
    taxYearBasedProjectPlan: candidate.taxYearBasedProjectPlan?.map((plan) => ({ ...plan })),
  };
}

/**
 * Creates a deep clone of a CashFlow object.
 */
function cloneCashFlow(cashFlow: CashFlow): CashFlow {
  return {
    ...cashFlow,
    bondPayments: cashFlow.bondPayments?.map(cloneBondPayment) ?? [],
  };
}

/**
 * Creates a deep clone of a BondPaymentCashFlowRecord object.
 */
function cloneBondPayment(bondPayment: BondPaymentCashFlowRecord): BondPaymentCashFlowRecord {
  return { ...bondPayment };
}

/**
 * Creates a clone of a Candidate prepared for reprocessing.
 *
 * This is a convenience function that clones the candidate and resets
 * processing-related flags so it can be run through the processor again.
 *
 * IMPORTANT: This resets cashFlows and results to ensure a fresh processing run.
 * This is necessary for debug capture to work correctly - the debug system
 * needs to capture the processing state as it happens, not stale data.
 *
 * @param candidate - The candidate to clone
 * @returns A cloned candidate ready for reprocessing
 */
export function cloneCandidateForReprocessing(candidate: Candidate): Candidate {
  const clone = cloneCandidate(candidate);
  clone.isProcessed = false;
  // Reset cashFlows to ensure fresh processing (needed for debug capture)
  // The debug system needs to capture processing state as it happens, not stale data
  clone.cashFlows = [];
  // Reset revenueMap to ensure fresh revenue calculation
  // This ensures the revenue distribution happens from scratch, allowing debug capture
  clone.revenueMap = {};
  // Reset results to ensure fresh evaluation
  clone.results = StateManager.createFreshResults();
  return clone;
}
